/**
 * Sample Core - shared primitives for the free sample flow
 *
 * Extracted from sample-flow.js so the PDP flow and the collection-page
 * quick buy button run the SAME cart, discount and Netlify logic. The
 * ordering in here (discount cookie before the cart add, attach after) is
 * load bearing: Shopify only attaches a code to the cart during a real cart
 * write, and only when the code already applies to something in it.
 *
 * Exposed as window.knpSampleCore. Loaded on every page that offers a sample.
 */
(function () {
  'use strict';

  if (window.knpSampleCore) return;

  /* ── Configuration ── */
  var CONFIG = {
    klaviyoListId: 'VPaTt9',
    netlifyBase: 'https://willowy-paprenjak-646e0e.netlify.app/.netlify/functions/sample',
    discountCode: 'FREESAMPLE',
    localStorageKeys: {
      member: 'knp_member',
      email: 'knp_email',
      code: 'knp_code'
    }
  };

  /* ── Helpers ── */

  function withTimeout(promise, ms) {
    return Promise.race([
      promise,
      new Promise(function (_, reject) {
        setTimeout(function () { reject(new Error('timeout')); }, ms);
      })
    ]);
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function getSectionsToRender() {
    return [
      { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
      { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
      { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
    ];
  }

  function getSectionInnerHTML(html, selector) {
    selector = selector || '.shopify-section';
    var el = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
    return el ? el.innerHTML : '';
  }

  /**
   * Replicates the theme's cart drawer open pattern from
   * ProductCard.renderContents (app.js) and ProductForm.renderContents (product.js)
   */
  function renderCartSections(parsedState) {
    getSectionsToRender().forEach(function (section) {
      var container = document.getElementById(section.id);
      if (!container) return;

      // The header toggle needs special handling: when the cart was empty
      // the theme renders no .thb-item-count inside it, and the generic
      // fallback below would overwrite the WHOLE toggle (cart icon included)
      // with just the count. Swap or append only the bubble element.
      if (section.id === 'cart-drawer-toggle') {
        var freshDoc = new DOMParser().parseFromString(parsedState.sections[section.section], 'text/html');
        var freshBubble = freshDoc.querySelector('.thb-item-count');
        if (freshBubble) {
          var existingBubble = container.querySelector('.thb-item-count');
          if (existingBubble) existingBubble.replaceWith(freshBubble);
          else container.appendChild(freshBubble);
        }
        return;
      }

      var el = container.querySelector(section.selector) || container;
      el.innerHTML = getSectionInnerHTML(parsedState.sections[section.section], section.selector);

      if (typeof Cart !== 'undefined') {
        new Cart().renderContents(parsedState);
      }

      // Re-bind cart drawer events
      if (section.id === 'Cart-Drawer') {
        if (typeof el.removeProductEvent === 'function') el.removeProductEvent();
        if (typeof el.notesToggle === 'function') el.notesToggle();
        if (typeof el.updateFreeShipping === 'function') el.updateFreeShipping();
      }
    });
  }

  function openCartDrawer() {
    var cartDrawer = document.getElementById('Cart-Drawer');
    if (!cartDrawer) return;

    document.body.classList.add('open-cc');
    document.body.classList.add('open-cart');
    cartDrawer.classList.add('active');

    var recs = cartDrawer.querySelector('.product-recommendations--full');
    if (recs) recs.classList.add('active');

    if (typeof dispatchCustomEvent === 'function') {
      dispatchCustomEvent('cart-drawer:open');
    }
  }

  /**
   * POST to /cart/add.js with section rendering, then open cart drawer.
   * Returns a promise that resolves with the response JSON.
   */
  function addToCart(variantId, skipDrawer) {
    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    formData.append('sections', getSectionsToRender().map(function (s) { return s.section; }));
    formData.append('sections_url', window.location.pathname);

    return fetch(window.theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript'
      },
      body: formData
    })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.status) {
          throw new Error(response.description || 'Cart add failed');
        }

        renderCartSections(response);
        if (!skipDrawer) openCartDrawer();

        if (typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        }

        return response;
      });
  }

  /**
   * Apply discount code by fetching /discount/CODE.
   * Shopify sets a session cookie so the discount auto-applies at checkout.
   * redirect: 'manual' because the response is a redirect to the homepage;
   * the Set-Cookie on the redirect is all we need, not the homepage HTML.
   */
  function applyDiscountCode(code) {
    var discountCode = code || CONFIG.discountCode;
    if (!discountCode) return Promise.resolve();
    return fetch('/discount/' + discountCode, { credentials: 'same-origin', redirect: 'manual' })
      .catch(function () {});
  }

  /* ── Identity ── */

  function isMember() {
    try { return localStorage.getItem(CONFIG.localStorageKeys.member) === 'true'; }
    catch(e) { return false; }
  }

  function getMemberEmail() {
    try { return localStorage.getItem(CONFIG.localStorageKeys.email) || ''; }
    catch(e) { return ''; }
  }

  function setMember(email) {
    try {
      localStorage.setItem(CONFIG.localStorageKeys.member, 'true');
      localStorage.setItem(CONFIG.localStorageKeys.email, email);
    } catch(e) {}
  }

  /**
   * The member's own discount code, remembered across pages so the cookie
   * can be set correctly before a claim round trip has happened.
   */
  function getStoredCode() {
    try { return localStorage.getItem(CONFIG.localStorageKeys.code) || ''; }
    catch(e) { return ''; }
  }

  function setStoredCode(code) {
    if (!code || code === CONFIG.discountCode) return;
    try { localStorage.setItem(CONFIG.localStorageKeys.code, code); } catch(e) {}
  }

  /**
   * Tells sample-reconcile.js a free claim is settling for this variant, so
   * every cart render shows the final numbers immediately instead of £2
   * flicking to £0 when the discount attaches. Cleared by the reconciler
   * once the cart really shows the line free; self-expires on failure.
   */
  function setPendingFree(variantId) {
    try {
      localStorage.setItem('knp_pending_free', JSON.stringify({ v: variantId, ts: Date.now() }));
    } catch(e) {}
  }

  /* ── Klaviyo JS identify ── */

  function klaviyoSubscribe(email) {
    // Identify the profile via Klaviyo's onsite JS (loaded as app embed).
    // List subscription is handled server-side by the Netlify function.
    return new Promise(function (resolve) {
      var attempts = 0;
      function tryKlaviyo() {
        if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
          window.klaviyo.identify({ email: email })
            .then(function () { resolve(); })
            .catch(function () { resolve(); });
        } else if (window._learnq) {
          // Legacy Klaviyo SDK fallback
          window._learnq.push(['identify', { '$email': email }]);
          resolve();
        } else if (attempts < 20) {
          attempts++;
          setTimeout(tryKlaviyo, 250);
        } else {
          resolve();
        }
      }
      tryKlaviyo();
    });
  }

  /* ── Netlify function ── */

  function netlifySubmit(email, variantId, productTitle) {
    if (!CONFIG.netlifyBase) return Promise.resolve({ success: true, credits_remaining: 0 });

    return fetch(CONFIG.netlifyBase, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email,
        variant_id: variantId,
        product_title: productTitle
      })
    })
      .then(function (r) { return r.json(); });
  }

  function netlifyCheckCredit(email, variantId) {
    if (!CONFIG.netlifyBase) return Promise.resolve({ credits_remaining: 1 });

    var payload = { email: email };
    if (variantId) payload.variant_id = variantId;

    return fetch(CONFIG.netlifyBase + '/check', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) { return r.json(); });
  }

  /**
   * Re-issue a cart write so Shopify attaches the discount cookie to the
   * cart, then re-render the cart sections showing it. Needed whenever the
   * cookie or price rule lands AFTER the item was added: attachment only
   * happens during a real cart mutation.
   */
  function attachDiscountToCart(itemKey) {
    if (!itemKey) return Promise.resolve();
    var sections = getSectionsToRender().map(function (s) { return s.section; });
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: itemKey, quantity: 1, sections: sections, sections_url: window.location.pathname }),
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        if (response.sections) {
          renderCartSections(response);
        }
      })
      .catch(function () {});
  }

  /**
   * Re-scope the shared FREESAMPLE price rule to the variant being claimed.
   * Without this, a returning member checks out against whichever variant the
   * last new subscriber claimed. Fire and forget: it must settle before the
   * customer reaches checkout, not before the drawer opens.
   */
  function netlifyClaim(email, variantId) {
    if (!CONFIG.netlifyBase || !email || !variantId) return Promise.resolve();

    return fetch(CONFIG.netlifyBase + '/claim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, variant_id: variantId })
    })
      .then(function (r) { return r.json(); })
      .catch(function () {});
  }

  /**
   * The free sample add, in the one order that works.
   *
   * The discount cookie must land BEFORE the cart add: Shopify only attaches
   * a code to the cart during a real cart write, and only when the code
   * applies to something in it. With the cookie first, the add's own section
   * render comes back already discounted, so the drawer shows FREE with no
   * extra round trips. The claim re-scope runs in the background as a
   * backstop; /check pre-scopes the rule at page view.
   *
   * openDrawer defaults to true (the PDP pattern). Collection quick buy
   * passes false: grid adds confirm in place so multi-sample browsing is
   * not interrupted by the drawer on every press.
   */
  function addSample(variantId, isFree, discountCode, openDrawer) {
    if (isFree) setPendingFree(variantId);
    var claim = isFree ? netlifyClaim(getMemberEmail(), variantId) : Promise.resolve();
    var appliedCode = isFree ? (discountCode || getStoredCode()) : null;
    var setup = isFree ? applyDiscountCode(appliedCode) : Promise.resolve();

    // Give a warm claim the chance to finish re-scoping before the add, so
    // the add's own section render comes back already free instead of £2
    // healing a beat later. A cold start loses the race and the heal below
    // still covers it — this trades up to 900ms of spinner for no £2 flash.
    var grace = isFree
      ? Promise.race([claim, new Promise(function (r) { setTimeout(r, 900); })])
      : Promise.resolve();

    var healDone;
    var result = setup
      .then(function () { return grace; })
      .then(function () { return addToCart(variantId, true); })
      .then(function (addResponse) {
        if (openDrawer !== false) openCartDrawer();
        if (isFree) {
          // The claim response carries the member's own code (immune to
          // other claimants). Skip the round trips entirely when the add
          // already rendered free under the right cookie; otherwise swap
          // the cookie if needed and one re-write attaches the discount.
          healDone = claim.then(function (claimResult) {
            var code = claimResult && claimResult.discount_code;
            if (code) setStoredCode(code);
            var item = addResponse && (addResponse.items ? addResponse.items[0] : addResponse);
            var alreadyFree = item && item.final_line_price === 0;
            if (alreadyFree && (!code || code === appliedCode)) return;
            var swap = (code && code !== appliedCode) ? applyDiscountCode(code) : Promise.resolve();
            return swap.then(function () {
              return attachDiscountToCart(addResponse && addResponse.key);
            });
          });
          // Callers that navigate away (view basket) wait on this so the
          // customer lands on a cart that already reads free
          window.knpSampleCore.lastHeal = healDone;
        }
        return addResponse;
      });
    return result;
  }

  window.knpSampleCore = {
    CONFIG: CONFIG,
    withTimeout: withTimeout,
    isValidEmail: isValidEmail,
    getSectionsToRender: getSectionsToRender,
    getSectionInnerHTML: getSectionInnerHTML,
    renderCartSections: renderCartSections,
    openCartDrawer: openCartDrawer,
    addToCart: addToCart,
    applyDiscountCode: applyDiscountCode,
    isMember: isMember,
    getMemberEmail: getMemberEmail,
    setMember: setMember,
    getStoredCode: getStoredCode,
    setStoredCode: setStoredCode,
    setPendingFree: setPendingFree,
    lastHeal: null,
    klaviyoSubscribe: klaviyoSubscribe,
    netlifySubmit: netlifySubmit,
    netlifyCheckCredit: netlifyCheckCredit,
    netlifyClaim: netlifyClaim,
    attachDiscountToCart: attachDiscountToCart,
    addSample: addSample
  };
})();
